import { useCallback, useEffect, useMemo, useState } from "react";
import { api, HttpError, type AuditEvent, type AuditPage } from "../api/client";

/**
 * Admin view of the audit trail.
 *
 * <p>Deliberately a table, not a dashboard: an operator arrives here with a
 * question about a specific person or a specific hour ("who hid that image?"),
 * and a chart of event counts answers none of them. The stat row at the top is
 * orientation only — how much happened, by how many people, and whether
 * anything failed — and every number in it is also reachable by filtering.
 */

const WINDOWS = [
  { key: "1h", label: "Last hour", ms: 3_600_000 },
  { key: "24h", label: "Last 24 hours", ms: 24 * 3_600_000 },
  { key: "7d", label: "Last 7 days", ms: 7 * 24 * 3_600_000 },
  { key: "30d", label: "Last 30 days", ms: 30 * 24 * 3_600_000 },
] as const;

type WindowKey = (typeof WINDOWS)[number]["key"];

export function AuditScreen() {
  const [windowKey, setWindowKey] = useState<WindowKey>("24h");
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState<AuditPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const ms = WINDOWS.find((w) => w.key === windowKey)!.ms;
    try {
      const res = await api.listAudit({
        from: new Date(Date.now() - ms).toISOString(),
        to: new Date().toISOString(),
        actor: actor || undefined,
        action: action || undefined,
        limit: 1000,
      });
      setPage(res);
    } catch (e) {
      setError(
        e instanceof HttpError && e.status === 403
          ? "Your account is not permitted to view the audit trail."
          : "Could not load the audit trail. Try again in a moment.",
      );
      setPage(null);
    } finally {
      setLoading(false);
    }
  }, [windowKey, actor, action]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const stats = useMemo(() => {
    if (!page) return null;
    const items = page.items;
    return {
      total: items.length,
      actors: new Set(items.map((e) => e.actor)).size,
      failed: items.filter((e) => !e.success).length,
      writes: items.filter((e) => WRITE_ACTIONS.has(e.action)).length,
    };
  }, [page]);

  return (
    <div className="audit">
      <div className="audit-hd">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="audit-title">Audit trail</h1>
        </div>
        <button className="chip" type="button" onClick={() => void refresh()}>
          Refresh
        </button>
      </div>

      <div className="audit-filters">
        <div className="audit-filter-group" role="group" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              className={`chip${windowKey === w.key ? " active" : ""}`}
              aria-pressed={windowKey === w.key}
              onClick={() => setWindowKey(w.key)}
            >
              {w.label}
            </button>
          ))}
        </div>

        <label className="audit-select">
          <span>Who</span>
          <select value={actor} onChange={(e) => setActor(e.target.value)}>
            <option value="">Everyone</option>
            {(page?.actors ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        <label className="audit-select">
          <span>What</span>
          <select value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All activity</option>
            {(page?.actions ?? []).map((a) => (
              <option key={a} value={a}>
                {ACTION_LABEL[a] ?? a}
              </option>
            ))}
          </select>
        </label>
      </div>

      {stats && (
        <div className="audit-stats">
          <Stat label="Events" value={stats.total} />
          <Stat label="People" value={stats.actors} />
          <Stat label="Changes" value={stats.writes} />
          <Stat label="Failed" value={stats.failed} tone={stats.failed > 0 ? "alert" : undefined} />
        </div>
      )}

      {error && <p className="audit-empty">{error}</p>}
      {loading && !error && <p className="audit-empty">Loading…</p>}

      {!loading && !error && page && page.items.length === 0 && (
        <p className="audit-empty">Nothing recorded in this window.</p>
      )}

      {!loading && !error && page && page.items.length > 0 && (
        <>
          {page.truncated && (
            <p className="audit-note">
              Showing the {page.count} most recent events — there are more in this window. Narrow
              the time range or filter by person to see the rest.
            </p>
          )}
          <div className="audit-table-wrap">
            <table className="audit-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">What happened</th>
                  <th scope="col">Result</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((e) => (
                  <AuditRow
                    key={e.id}
                    event={e}
                    expanded={expanded === e.id}
                    onToggle={() => setExpanded(expanded === e.id ? null : e.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "alert" }) {
  return (
    <div className={`audit-stat${tone === "alert" ? " alert" : ""}`}>
      <div className="audit-stat-value">{value}</div>
      <div className="audit-stat-label">{label}</div>
    </div>
  );
}

function AuditRow({
  event,
  expanded,
  onToggle,
}: {
  event: AuditEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className={expanded ? "expanded" : ""} onClick={onToggle}>
        <td className="audit-when">
          <time dateTime={event.createdAt} title={event.createdAt}>
            {fmtClock(event.createdAt)}
          </time>
          <span className="audit-date">{fmtDay(event.createdAt)}</span>
        </td>
        <td className="audit-who">{event.actor}</td>
        <td className="audit-what">{describe(event)}</td>
        <td className="audit-result">
          {/* Status is never colour alone — the word carries it, the dot reinforces. */}
          <span className={`audit-badge${event.success ? "" : " fail"}`}>
            <span className="dot" aria-hidden="true" />
            {event.success ? "OK" : `Failed${event.statusCode ? ` ${event.statusCode}` : ""}`}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="audit-detail-row">
          <td colSpan={4}>
            <dl className="audit-detail">
              <Field label="Request" value={[event.method, event.path].filter(Boolean).join(" ")} />
              <Field label="Entity" value={entityText(event)} />
              <Field label="IP address" value={event.ip} />
              <Field label="Browser" value={event.userAgent} />
              <Field label="Recorded" value={event.createdAt} />
              {event.detail &&
                Object.entries(event.detail).map(([k, v]) => (
                  <Field key={k} label={humanize(k)} value={renderValue(v)} />
                ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="audit-field">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

const WRITE_ACTIONS = new Set(["create", "update", "delete", "admin"]);

const ACTION_LABEL: Record<string, string> = {
  login: "Sign-in",
  logout: "Sign-out",
  nav: "Navigation",
  create: "Created",
  update: "Changed",
  delete: "Removed",
  admin: "Admin operation",
};

/**
 * Turn a row into a sentence an operator can scan. The generic fallback is
 * "<verb> <entity>" so an action added on the API side still renders something
 * sensible here without a frontend change.
 */
function describe(e: AuditEvent): string {
  const d = e.detail ?? {};
  const str = (k: string): string | null => {
    const v = d[k];
    return v === null || v === undefined ? null : String(v);
  };

  switch (e.action) {
    case "login": {
      const method = str("method");
      const reason = str("reason");
      const base = `Signed in${method ? ` with ${method}` : ""}`;
      return e.success ? base : `Sign-in rejected${reason ? ` — ${reason}` : ""}`;
    }
    case "logout":
      return "Signed out";
    case "nav": {
      const screen =
        e.entityId === "calendar" ? "Calendar" : e.entityId === "audit" ? "Audit trail" : "Recent";
      const date = str("date");
      const img = str("imageId");
      if (img) return `Opened ${screen} on image ${short(img)}`;
      return `Viewed ${screen}${date ? ` for ${date}` : ""}`;
    }
    case "admin":
      return `Ran ${humanize(str("operation") ?? "operation").toLowerCase()}`;
    default: {
      const field = str("field");
      const to = str("to");
      const from = str("from");
      const target = entityText(e) ?? "record";
      if (field === "speciesOverride") {
        return `Set species on ${target}: ${from ?? "none"} → ${to ?? "none"}`;
      }
      if (field === "hidden") return `${to === "true" ? "Hid" : "Unhid"} ${target}`;
      if (field === "liked") return `${to === "true" ? "Liked" : "Unliked"} ${target}`;
      if (field === "comment") return `Commented on ${target}`;
      if (e.action === "create") return `Uploaded ${target}`;
      return `${ACTION_LABEL[e.action] ?? e.action} ${target}`;
    }
  }
}

function entityText(e: AuditEvent): string | null {
  if (!e.entityType) return null;
  if (!e.entityId) return e.entityType;
  if (e.entityType === "image") return `image ${short(e.entityId)}`;
  return `${e.entityType} ${e.entityId}`;
}

/** UUIDs are unreadable in a table; the first block is enough to correlate. */
function short(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

function humanize(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return "none";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function fmtClock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
